import {Utils} from "./Utils";

export interface ProfileConfig {
    infoWrapper: HTMLElement,
    formWrapper: HTMLElement,
    editButton: HTMLElement,
    cancelButton: HTMLElement,
    passwordFields: HTMLElement,
    userpic: HTMLElement
}

export class Profile {
    private readonly elements: {
        infoWrapper: HTMLElement;
        formWrapper: HTMLElement;
        editButton: HTMLElement;
        cancelButton: HTMLElement;
        passwordFields: HTMLElement;
        userpic: HTMLElement;
    };
    protected passwordLink: HTMLElement;
    protected loadedUserpic: string;

    constructor(options: ProfileConfig) {
        this.elements = options;
        this.init();
    }

    public showEditForm(): void {
        const {formWrapper, infoWrapper, passwordFields, userpic} = this.elements;

        formWrapper.classList.remove(`is-hidden`);
        if (passwordFields) this.renderChangePassword();
        if (userpic) {
            this.renderPhoto();
            this.renderDropzone();
        }
        infoWrapper.classList.add(`is-hidden`);
    }

    public hideEditForm(): void {
        if (this.passwordLink) this.passwordLink.remove();
        this.elements.infoWrapper.classList.remove(`is-hidden`);
        this.elements.formWrapper.classList.add(`is-hidden`);
    }

    public renderPhoto(): void {
        const photos = this.elements.userpic.querySelectorAll(`img`);

        if (photos.length > 1) {
            const defaultPhoto = photos[photos.length - 1],
                newPhoto = photos[0];

            this.createDeletePhotoLink(newPhoto);
            defaultPhoto.remove();
        } else {
            photos.forEach((img) => this.updateUserpic(img));
        }
    }

    public createDeletePhotoLink(newPhoto: HTMLElement): void {
        const link = document.createElement(`a`);
        link.classList.add(`auth__link`,  `auth__link--center`);
        link.setAttribute(`href`, `javascript:void(0);`);
        link.textContent = this.deletePhotoText;

        newPhoto.insertAdjacentElement('afterend', link);
        link.addEventListener('click', () => this.deletePhotoHandler(link, newPhoto));
    }

    public deletePhotoHandler(link: HTMLElement, newPhoto: HTMLElement): void {
        const input = this.elements.userpic.querySelector(`[name="profile[photo]"]`) as HTMLInputElement;

        this.renderUserpic(newPhoto);
        newPhoto.style.display = `none`;
        link.remove();
        input.value = ``;
    }

    public renderDropzone(): void {
        const input = this.elements.userpic.querySelector(`input[type=file]`) as HTMLElement;

        if (input && !input.hasAttribute(`id`)) {
            input.setAttribute(`id`, `user-profile-dropzone`);
            input.style.display = `none`;
            input.insertAdjacentHTML('afterend', this.dropzoneTemplate);
        }
    }

    public changeUserPhotos(): void {
        const photo = this.elements.infoWrapper.querySelector(`img`);
        if (photo) this.updateUserpic(photo);
    }

    public updateUserpic(img: HTMLElement): void {
        let url = img.getAttribute(`src`);
        if (url.includes(`/wa-content/img/userpic96.jpg`) || url.includes(`/wa-content/img/userpic96@2x.jpg`)) {
            this.renderUserpic(img);
        }
    };

    public renderUserpic(element: HTMLElement): void {
        const changeUserpic = (element: HTMLElement, text: string | void) => {
            if (!text) return;
            element.insertAdjacentHTML('afterend', text);
            element.remove();
        };

        this.getUserpic()
            .then(text => changeUserpic(element, text));
    }

    public getUserpic(): Promise<string | void> {
        if (this.loadedUserpic) return new Promise((resolve, reject) => resolve(this.loadedUserpic));
        return fetch(`${window.theme.url}img/svg/userpic.svg`)
            .then(response => {return response.text()})
            .then(text => {this.loadedUserpic = text; return this.loadedUserpic})
            .catch(error => console.error('Error:', error));
    }

    public renderChangePassword(): void {
        const
            {passwordFields} = this.elements,
            link = document.createElement(`a`);

        link.classList.add(`auth__link`);
        link.setAttribute(`href`, `javascript:void(0);`);
        link.textContent = this.changePasswordText;

        passwordFields.classList.add(`is-hidden`);
        passwordFields.insertAdjacentElement('afterend', link);
        link.addEventListener('click', () => this.changePasswordHandler(link));
        this.passwordLink = link;
    }

    changePasswordHandler(link: HTMLElement) {
        link.remove();
        this.elements.passwordFields.classList.remove(`is-hidden`);
    }

    protected init(): void {
        const {infoWrapper, formWrapper, editButton, cancelButton} = this.elements;
        if (!infoWrapper || !formWrapper) return;

        if (editButton) editButton.addEventListener('click', () => this.showEditForm());
        if (cancelButton) cancelButton.addEventListener('click', () => this.hideEditForm());
        this.changeUserPhotos();
    }

    public get changePasswordText(): string {
        return (Utils.getLang() === `ru_ru`) ? `Изменить пароль` : `Change password`;
    }

    public get deletePhotoText(): string {
        return (Utils.getLang() === `ru_ru`) ? `Удалить фото` : `Delete photo`;
    }

    public get dropzoneTemplate(): string {
        let buttonText = (Utils.getLang() === `ru_ru`) ? `Выберите файл` : `Select a file`,
            hintText = (Utils.getLang() === `ru_ru`) ? `Допустимые форматы:` : `Acceptable formats:`;

        return `
            <label class="drop-zone" for="user-profile-dropzone">
                <span class="drop-zone__button">${buttonText}</span>
                <p class="drop-zone__hint">${hintText}  .jpg, .png, .gif</p>
            </label>
        `;
    }
}